import { Accordion, AccordionItem } from '@heroui/react';
import { ReactNode, useMemo } from 'react';

import {
  logCardItemClasses,
  type LogCardAccordionItemClasses
} from './accordionStyles';

type AccordionItemClassNames = LogCardAccordionItemClasses;

interface LogCardProps {
  /** Title to display in the accordion header */
  title: ReactNode;
  /** Text value for accessibility (required when title is not a plain string) */
  textValue?: string;
  /** Content to display inside the accordion */
  children: ReactNode;
  /** Rendered after children inside the same accordion panel (e.g. log annotations). */
  footer?: ReactNode;
  /** Whether the accordion should be expanded by default */
  defaultExpanded?: boolean;
  /** Controlled expanded state (used for expand/collapse all) */
  isExpanded?: boolean;
  /** Called when the user toggles this card while in controlled mode */
  onExpandedChange?: (expanded: boolean) => void;
  /** Accordion item key — must be unique when multiple cards share a parent list */
  itemKey?: string;
  /** Override accordion item classes (e.g. Monitor drawer inner log rows) */
  itemClasses?: AccordionItemClassNames;
  /** Additional CSS classes for the wrapper */
  className?: string;
}

/**
 * Shared wrapper component for log cards with consistent accordion styling
 * Used by ConfigurationCard, InputCard, GuardrailCard, MessageCard, ToolCallCard
 */
export function LogCard({
  title,
  textValue,
  children,
  footer,
  defaultExpanded = true,
  isExpanded,
  onExpandedChange,
  itemKey = '1',
  itemClasses = logCardItemClasses,
  className = 'mb-2'
}: LogCardProps) {
  const isControlled = isExpanded !== undefined;
  const selectedKeys = useMemo(
    () => (isExpanded ? new Set([itemKey]) : new Set<string>()),
    [isExpanded, itemKey]
  );
  const uncontrolledDefaultKeys = useMemo(
    () => (defaultExpanded ? [itemKey] : []),
    [defaultExpanded, itemKey]
  );

  return (
    <div className={className}>
      <Accordion
        selectionMode='single'
        selectedKeys={isControlled ? selectedKeys : undefined}
        defaultSelectedKeys={isControlled ? undefined : uncontrolledDefaultKeys}
        onSelectionChange={
          isControlled && onExpandedChange
            ? keys => {
                const nextKeys =
                  keys === 'all' ? new Set([itemKey]) : (keys as Set<string>);
                onExpandedChange(nextKeys.has(itemKey));
              }
            : undefined
        }
        itemClasses={itemClasses}
      >
        <AccordionItem key={itemKey} title={title} textValue={textValue}>
          {children}
          {footer}
        </AccordionItem>
      </Accordion>
    </div>
  );
}
